import {Component, OnInit} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {User} from '../../../user/model/User';
import {Constant} from '../../../global/util/Constant';
import {LocalStorage} from '../../../global/util/LocalStorage';
import {Dialog} from '../../../global/util/Dialog';
import {InformType} from '../../../global/util/enum/InformType';
import HttpStatusCode from '../../../global/util/HttpStatusCode';
import {AuthService} from '../../../user/service/auth/auth.service';
import {TranslateService} from '@ngx-translate/core';
import {ApplicationService} from '../../../user/service/application.service';
import {ActivatedRoute, Router} from '@angular/router';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {PasswordLevel} from '../../../global/util/PasswordLevel';
import {Passwordsecuritylevel} from '../../../user/model/Passwordsecuritylevel';

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.css']
})
export class ChangePasswordComponent implements OnInit {
  changePasswordForm: FormGroup;
  fieldTextType: boolean;
  showNewPassword: boolean;
  userId: any;
  loading: boolean;
  ROLE_SUPERADMIN = Constant.ROLE_SUPERADMIN;
  ROLE_LITE_ADMIN = Constant.ROLE_LITE_ADMIN;
  ROLE_ADMIN_NOTCER = Constant.ROLE_ADMIN_NOTCER;
  passwordSecurityLevel = new Passwordsecuritylevel();
  isSecurityHigh = true;
  isSecurityMedium = false;
  isSecurityLow = false;
  showConfirmPassword: boolean;
  username: any;
  user = new User();

  constructor(private fb: FormBuilder,
              private router: Router,
              private authenticationService: AuthService,
              private modalService: NgbModal,
              private applicationService: ApplicationService,
              private translate: TranslateService) {
    Dialog.createInstance(this.modalService);
    this.createForm();
  }

  ngOnInit(): void {
    this.userId = history.state.userId;
    this.username = history.state.username;
    this.user.username = this.username;
    if (this.userId !== undefined && this.username !== undefined) {
      this.getHighPasswordLevel(this.userId);
    } else {
      this.router.navigate(['login']).then();
    }
  }

  passwordComplexityMedium(control: AbstractControl): { [key: string]: boolean } | null {
    const password = control.value;
    if (!password) {
      return null;
    }
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const hasNoSpaces = !/\s/.test(password);
    const valid = hasNoSpaces;
    return valid ? null : {passwordComplexity: true};
  }


  passwordMatchValidator(group: FormGroup): { [key: string]: boolean } | null {
    const newPassword = group.get('newPassword').value;
    const confirmPassword = group.get('confirmPassword').value;
    return newPassword === confirmPassword ? null : {mismatch: true};
  }

  submit() {
    const newPasswordControl = this.changePasswordForm.get('newPassword');
    const confirmPasswordControl = this.changePasswordForm.get('confirmPassword');
    const isFormValid = this.changePasswordForm.valid;
    const newPassword = newPasswordControl.value;
    const passwordRequirementsMet =
      this.isSecurityHigh ? (this.passwordControls.isValidLength &&
          this.passwordControls.hasUpperCase &&
          this.passwordControls.hasLowerCase &&
          this.passwordControls.hasNumber &&
          this.passwordControls.hasNoSpaces &&
          this.passwordControls.hasSpecialChar) :
        (this.isSecurityMedium ? (this.passwordControls.isValidLength &&
            this.passwordControls.hasUpperCase &&
            this.passwordControls.hasLowerCase &&
            this.passwordControls.hasNumber &&
            this.passwordControls.hasNoSpaces) :
          this.passwordControls.isValidLength && this.passwordControls.hasNoSpaces);
    if (isFormValid && passwordRequirementsMet) {
      const url = Constant.ROOT_API_V1.concat('/public/update-password/');
      const formData = new FormData();
      formData.append('username', this.username);
      formData.append('newPassword', newPassword);
      this.applicationService.post(url, formData).subscribe((next: any) => {
        if (next.passwordChanged === 'true') {
          this.user.password = newPassword;
          this.login2(this.user);
        } else {
          this.showWarning('MESSAGE.PASSWORD-ENTERED-IS-NOT-CORRECT');
        }
      }, error => {
        this.showDanger('MESSAGE.DO-NOT-UPDATE-PASSWORD');
        this.router.navigate(['login']).then();
      });
    } else {
      this.changePasswordForm.markAllAsTouched();
      this.handleFormErrors(newPasswordControl, confirmPasswordControl);
    }
  }

  private showWarning(messageKey: string) {
    Dialog.show(InformType.Warning, this.translate.instant('TITLE.CHANGE-PASSWORD'), this.translate.instant(messageKey));
  }

  private showDanger(messageKey: string) {
    Dialog.show(InformType.Danger, this.translate.instant('TITLE.CHANGE-PASSWORD'), this.translate.instant(messageKey));
  }

  private handleFormErrors(newPasswordControl: any, confirmPasswordControl: any) {
    this.changePasswordForm.markAllAsTouched();
    if (newPasswordControl.invalid) {
      this.showDanger('MESSAGE.PASSWORD-REQUIRED-MUST-MEET-REQUIREMENTS');
    } else if (newPasswordControl.value !== confirmPasswordControl.value) {
      this.showDanger('MESSAGE.PASSWORDS-DO-NO-MATCH');
    } else {
      this.showDanger('MESSAGE.PASSWORD-REQUIRED-MUST-MEET-REQUIREMENTS');
    }
  }

  get passwordControls() {
    this.passwordSecurityLevel.level = this.passwordSecurityLevel.level !== null ? this.passwordSecurityLevel.level : 3;
    const specialCharRegex = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/;
    switch (this.passwordSecurityLevel.level) {
      case PasswordLevel.PASSWORD_HIGH:
        this.isSecurityHigh = true;
        this.isSecurityMedium = false;
        this.isSecurityLow = false;
        return {
          hasUpperCase: /[A-Z]/.test(this.changePasswordForm.get('newPassword').value),
          hasLowerCase: /[a-z]/.test(this.changePasswordForm.get('newPassword').value),
          hasNumber: /[0-9]/.test(this.changePasswordForm.get('newPassword').value),
          hasSpecialChar: specialCharRegex.test(this.changePasswordForm.get('newPassword').value),
          hasNoSpaces: !/\s/.test(this.changePasswordForm.get('newPassword').value),
          isValidLength: this.changePasswordForm.get('newPassword').value?.length > 15
        };
      case PasswordLevel.PASSWORD_MEDIUM:
        this.isSecurityHigh = false;
        this.isSecurityMedium = true;
        this.isSecurityLow = false;
        return {
          hasUpperCase: /[A-Z]/.test(this.changePasswordForm.get('newPassword').value),
          hasLowerCase: /[a-z]/.test(this.changePasswordForm.get('newPassword').value),
          hasNumber: /[0-9]/.test(this.changePasswordForm.get('newPassword').value),
          hasNoSpaces: !/\s/.test(this.changePasswordForm.get('newPassword').value),
          isValidLength: this.changePasswordForm.get('newPassword').value?.length > 11
        };
      case PasswordLevel.PASSWORD_LOW:
        this.isSecurityHigh = false;
        this.isSecurityMedium = false;
        this.isSecurityLow = true;
        return {
          hasNoSpaces: !/\s/.test(this.changePasswordForm.get('newPassword').value),
          isValidLength: this.changePasswordForm.get('newPassword').value?.length > 7
        };
    }
    this.changePasswordForm.get('newPassword').updateValueAndValidity();
  }

  public toggleFieldTextType(): void {
    this.fieldTextType = !this.fieldTextType;
  }


  toggleShowNewPassword() {
    return this.showNewPassword = !this.showNewPassword;
  }

  login2(user: User) {
    this.loading = true;
    this.authenticationService.login(user).subscribe(data => {
      this.loading = false;
      const onlyApi = data.headers.headers.get('onlyapi')[Constant.ZERO_INDEX];
      const active = data.headers.headers.get('active')[Constant.ZERO_INDEX];
      const deleted = data.headers.headers.get('deleted')[Constant.ZERO_INDEX];
      const userType = data.headers.headers.get('usertype')[Constant.ZERO_INDEX];
      const personId = data.headers.headers.get('personid')[Constant.ZERO_INDEX];
      LocalStorage.setItem('userType', userType);
      if (Constant.ONLY_API_OPTION === onlyApi) {
        Dialog.show(InformType.Info, this.translate.instant('TITLE.LOGIN'), this.translate.instant('MESSAGE.USER-NOT-HAVE-NECESSARY-PERMISSIONS'));
        return;
      } else if (Constant.INACTIVE === +active) {
        Dialog.show(InformType.Warning, this.translate.instant('TITLE.LOGIN'), this.translate.instant('MESSAGE.PERSON-ASSOCIATED-IS-INACTIVE'));
        return;
      } else if (Constant.DELETED === +deleted) {
        Dialog.show(InformType.Warning, this.translate.instant('TITLE.LOGIN'), this.translate.instant('MESSAGE.PERSON-ASSOCIATED-IS-DELETED'));
        return;
      }
      this.applicationService.setUserLoggedIn(true);
      this.loadStaticData();
      Dialog.show(InformType.Success, this.translate.instant('TITLE.AUTHENTICATION'), this.translate.instant('MESSAGE.WELCOME-IOFESIGN'));
    }, error => {
      this.loading = false;
      this.applicationService.setUserLoggedIn(false);
      if (error.status === HttpStatusCode.UNAUTHORIZED) {
        Dialog.show(InformType.Warning, this.translate.instant('TITLE.LOGIN'), this.translate.instant('MESSAGE.ACCESS-PERMISSIONS-PROBLEMS'));
      } else if (error.status === HttpStatusCode.FORBIDDEN) {
        Dialog.show(InformType.Warning, this.translate.instant('TITLE.LOGIN'), this.translate.instant('MESSAGE.CREDENTIAL-ARE-INCORRECT'));
      } else if (error.status === HttpStatusCode.LOCKED) {
        Dialog.show(InformType.Warning, this.translate.instant('TITLE.LOGIN'), this.translate.instant('MESSAGE.YOUR-USER-IS-SUSPENDED-PLEASE-CONTACT-ADMINISTRATOR'));
      } else {
        Dialog.show(InformType.Danger, this.translate.instant('TITLE.LOGIN'));
      }
    });
  }

  loadStaticData() {
    this.loading = true;
    const url = Constant.ROOT_API_V1.concat('/staticdata');
    this.applicationService.get(url).subscribe((value: any) => {
      this.loading = false;
      LocalStorage.setUpEmbeddedInfo(value);
      if (this.hasRole(this.ROLE_SUPERADMIN)) {
        this.router.navigate(['enterprise']).then();
      } else if (this.hasRole(this.ROLE_LITE_ADMIN) || this.hasRole(this.ROLE_ADMIN_NOTCER)) {
        this.router.navigate(['workflow']).then();
      } else {
        this.router.navigate(['document']).then();
      }
    }, () => {
      Dialog.show(InformType.Danger, this.translate.instant('TITLE.HOME'), this.translate.instant('MESSAGE.ERROR-LOADING-INITIAL-DATA'));
    });
  }

  public hasRole(roleDescription: string) {
    return this.authenticationService.hasRole(roleDescription);
  }

  private createForm() {
    this.changePasswordForm = this.fb.group({
      newPassword: ['', [Validators.required, Validators.minLength(12), this.passwordComplexityMedium, this.noWhitespaceValidator()]],
      confirmPassword: ['', [Validators.required, Validators.minLength(12), this.noWhitespaceValidator()]]
    }, {validator: this.passwordMatchValidator});
  }

  getHighPasswordLevel(userId: any) {
    const url = Constant.ROOT_API_V1.concat('/public/level-security/').concat(userId).concat('/password');
    this.applicationService.get(url).subscribe((data: any) => {
      this.passwordSecurityLevel = data;
    }, error => {
      this.router.navigate(['login']).then();
    });
  }

  toggleShowConfirmPassword() {
    return this.showConfirmPassword = !this.showConfirmPassword;
  }

  noWhitespaceValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const isWhitespace = (control.value || '').trim().length !== control.value.length;
      const hasInnerSpaces = (control.value || '').includes(' ');
      const isValid = !isWhitespace && !hasInnerSpaces;
      return isValid ? null : {whitespace: true};
    };
  }

  validatePassword(): void {
    const newPasswordControl = this.changePasswordForm.get('newPassword');
    const repeatNewPasswordControl = this.changePasswordForm.get('confirmPassword');
    if (newPasswordControl && newPasswordControl.value) {
      newPasswordControl.setValue(newPasswordControl.value.replace(/\s/g, ''));
    }
    if (repeatNewPasswordControl && repeatNewPasswordControl.value) {
      repeatNewPasswordControl.setValue(repeatNewPasswordControl.value.replace(/\s/g, ''));
    }
    if (newPasswordControl.value !== repeatNewPasswordControl.value) {
      repeatNewPasswordControl.setErrors({mismatch: true});
    } else {
      repeatNewPasswordControl.setErrors(null);
    }
  }
}
